import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Subject} from 'rxjs';
import {AuthorizedObject} from '../../../api/dto';
import {Permission, PermissionAssignment, PermissionObjectType, PermissionSubjectType} from 'smag-commons/dto/api';
import {PermissionsService} from '../../../shared/permissions.service';
import {I18NService} from '../../../i18n/i18n.service';
import {MatDialog} from '@angular/material/dialog';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {map, takeUntil} from 'rxjs/operators';
import {PermissionAssignmentObject} from '../new-permission-assignment/new-permission-assignment.component';
import {ALL_USER_PERMISSION_PATTERNS, can} from '../../../shared/permissions';
import {UsersService} from '../../../shared/users.service';
import {isDefined} from '../../../commons/commons';
import {AssignmentSubject} from '../permission-assignments/permission-assignments.component';
import {
    PatternPermissionSettings,
    PermissionMode,
    PermissionSettings
} from '../permission-list/permission-list.component';

@Component({
    selector: 'app-perm-admin-users',
    templateUrl: './perm-admin-users.component.html',
    styleUrls: ['./perm-admin-users.component.scss']
})
export class PermAdminUsersComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    @ViewChild('subjectAssignments') private subjectAssignmentDialog: TemplateRef<any>;
    @ViewChild('newAssignment') private newAssignmentDialog: TemplateRef<any>;

    usersAssignable: boolean;
    assignments: AuthorizedObject<PermissionAssignment>[];

    constructor(private readonly permissionsService: PermissionsService,
                private readonly usersService: UsersService,
                private readonly i18n: I18NService,
                private readonly dialog: MatDialog,
                private readonly msgBoxService: MsgBoxService) {
        this.usersAssignable = null;
        this.assignments = null;
    }

    ngOnInit() {
        this.usersService.permissions.pipe(
            map(allPerms => can(allPerms, Permission.PERMISSIONS)),
            takeUntil(this.destroyed)
        ).subscribe(usersAssignable => {
            this.usersAssignable = usersAssignable;
            if (usersAssignable) {
                this.loadAssignments();
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    isDefined(value: any): boolean {
        return isDefined(value);
    }

    onClickNewAssignment() {
        this.dialog.open(this.newAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                subjectType: PermissionSubjectType.USER, // NOTE: only USER subjects currently supported
                object: {
                    type: PermissionObjectType.USER,
                    id: null,
                    permissionSettings: this.mkPermissionSettings(),
                    hint: this.i18n.translate('users')
                } as PermissionAssignmentObject,
            }
        }).afterClosed().subscribe((assignment: PermissionAssignment) => {
            if (assignment) {
                this.addAssignment(assignment)
                    .then(() => {
                        this.msgBoxService.info(this.i18n.translate('Permissions added'));
                        this.loadAssignments();
                    })
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Cannot add permissions'), err);
                    });
            }
        });
    }

    onClickSubjectAssignments(subject: AssignmentSubject) {
        this.dialog.open(this.subjectAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                subjectType: subject.type,
                subjectId: subject.id,
                hint: subject.hint
            }
        });
    }

    private loadAssignments() {
        this.assignments = null; // trigger loading spinner
        this.permissionsService.getPermissionsForObject(PermissionObjectType.USER, null).subscribe(assignments => {
            this.assignments = assignments;
        });
    }

    private addAssignment(assignment: PermissionAssignment): Promise<void> {
        return this.permissionsService.assignPermissions(assignment);
    }

    private mkPermissionSettings(): PermissionSettings {
        return {
            mode: PermissionMode.PATTERN,
            allPatterns: ALL_USER_PERMISSION_PATTERNS
        } as PatternPermissionSettings;
    }
}
